import { useInfiniteQuery, type QueryKey } from "@tanstack/react-query";
import type { CursorPageRequest } from "./types";

interface CursorPageLike {
  next_since: string;
  next_before_id: string | number | null;
}

interface UseCursorInfiniteOptions<TPage extends CursorPageLike> {
  /** TanStack query key for this list. */
  queryKey: QueryKey;
  /** Fetcher invoked per page. Receives `before` + `before_id` for older pages, or undefined for the first page. */
  fetchPage: (cursor: CursorPageRequest | undefined) => Promise<TPage>;
  /** Page size; passed through to fetchPage. */
  limit?: number;
  /** Conditionally disable (e.g. enabled=false on a closed modal). */
  enabled?: boolean;
  /** Refetch this often; TanStack's default disables polling. */
  staleTime?: number;
  /**
   * Seed cursor used for the first fetch. Useful when the initial paint is
   * already on screen from a sibling query (e.g. /trading-feed already
   * returned the first 10 history items) and the infinite query should
   * start fetching strictly older than that.
   */
  initialCursor?: CursorPageRequest;
}

/**
 * Wraps useInfiniteQuery for the `(before, before_id) → (next_since, next_before_id)`
 * cursor shape shared by /briefings, /materials/{id}/clusters, and
 * /materials/{id}/analysis/history. Pages are flat-mapped into one array
 * via `pages.flatMap((p) => p.items)` on the consumer side.
 */
export function useCursorInfinite<TPage extends CursorPageLike>({
  queryKey,
  fetchPage,
  limit,
  enabled,
  staleTime,
  initialCursor,
}: UseCursorInfiniteOptions<TPage>) {
  return useInfiniteQuery<TPage, Error, { pages: TPage[]; pageParams: unknown[] }, QueryKey, CursorPageRequest | undefined>({
    queryKey,
    queryFn: ({ pageParam }) => {
      const cursor = pageParam ?? initialCursor;
      return fetchPage(cursor ? { ...cursor, limit } : limit ? { limit } : undefined);
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage.next_before_id == null
        ? undefined
        : { before: lastPage.next_since, before_id: String(lastPage.next_before_id) },
    enabled,
    staleTime,
  });
}
